import { useCallback, useContext, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNewBlockAddedHandler } from './useNewBlockAddedHandler';
import { useCheckDocumentLockStatus } from 'components/editor/hooks/useCheckDocumentLockStatus';
import { DocumentSettingsContext } from '../SidePanel/DocumentSettings/DocumentDesignSettings/DocumentSettingsContext';
import { SelectionContext } from '../GridDndEditor/SelectedBlockInfoProvider';
import { useAppDispatch } from '../grid/reduxStore/Store';
import { setActiveSidePanel, setIsAdvancedSpacingModelOpen } from '../grid/reduxStore/blockStyleSettingsSlice';
import { TableSettingsTypes } from 'components/editor/SidePanel/content/TableSettings/types';
import { getDefaultPricingTableContent, getDefaultTableContent } from '../GridDndEditor/Block/Table/defaultData';
import { GridBlockType } from 'services/entities/BlockTypes';
import { gridDefaultDimensionsOnDrop, gridDefaultDimensionsOnTablePricing } from '../shared/gridConfig';
import { useEffectivePageSize } from 'hooks/useEffectivePageSize';
import useActiveSection from '../SectionsDrawer/useActiveSection';
import { selectSections } from '../grid/reduxStore/editorSlice';
import type { Block } from '../GridDndEditor/models/Block.model';
import { getCenteredY, getGreaterZIndexAvailable, resolveTargetSectionId } from './sectionViewportUtils';
import type { TableType } from '../grid/reduxStore/table.types';

type ClickToAddTableOptions = {
  isPricingTable: boolean;
  isCurrencySettingsEnabled?: boolean;
  // Pre-populated table content (e.g. an array variable already expanded into rows) —
  // when omitted, falls back to the normal empty default table/pricing table content.
  contentOverride?: TableType;
};

export default function useClickToAddTable() {
  const { documentSettings } = useContext(DocumentSettingsContext);
  const { selectedSectionId, setSelectedBlockInfoByWrapper } = useContext(SelectionContext);
  const { activeSection } = useActiveSection();
  const sections = useSelector(selectSections);
  const blockAddedHandler = useNewBlockAddedHandler();
  const checkDocumentLockStatus = useCheckDocumentLockStatus();
  const dispatch = useAppDispatch();
  const documentSettingsRef = useRef(documentSettings);
  const isAddingRef = useRef(false);
  const canvasWidth = useEffectivePageSize().width;
  const canvasWidthRef = useRef(canvasWidth);

  documentSettingsRef.current = documentSettings;
  canvasWidthRef.current = canvasWidth;

  return useCallback(
    async ({ isPricingTable, isCurrencySettingsEnabled, contentOverride }: ClickToAddTableOptions) => {
      if (isAddingRef.current) {
        return null;
      }

      isAddingRef.current = true;

      try {
        const targetSectionId = resolveTargetSectionId(selectedSectionId, activeSection, sections);

        if (!targetSectionId) {
          return null;
        }

        const { margin } = documentSettingsRef.current;
        const availableWidth = canvasWidthRef.current - margin.left - margin.right;
        const tableHeight = isPricingTable
          ? gridDefaultDimensionsOnTablePricing.height
          : gridDefaultDimensionsOnDrop[GridBlockType.TABLE].height;
        const blockConfig: Block = {
          x: margin.left,
          y: getCenteredY(targetSectionId, tableHeight),
          z: getGreaterZIndexAvailable(targetSectionId),
          width: availableWidth,
          height: tableHeight,
        };
        const tableContent =
          contentOverride ??
          (isPricingTable
            ? getDefaultPricingTableContent(Boolean(isCurrencySettingsEnabled), availableWidth)
            : getDefaultTableContent(availableWidth));
        const response = await blockAddedHandler(targetSectionId, { content: tableContent }, blockConfig, GridBlockType.TABLE);

        if (checkDocumentLockStatus(response.errorCode)) {
          return null;
        }

        const blockId = response.content.blockId;

        if (!blockId) {
          return null;
        }

        setSelectedBlockInfoByWrapper({ blockId, sectionId: targetSectionId });
        dispatch(setActiveSidePanel({ type: TableSettingsTypes.TABLE_DEFAULTS }));
        dispatch(setIsAdvancedSpacingModelOpen(false));

        return blockId;
      } finally {
        isAddingRef.current = false;
      }
    },
    [activeSection, blockAddedHandler, checkDocumentLockStatus, dispatch, sections, selectedSectionId, setSelectedBlockInfoByWrapper]
  );
}
